import { createCustomGameState } from "./customLayout.js";
import { createShuffledDeck } from "./deck.js";
import { canPlaceOnFoundation, canPlaceOnTableau, canPlaceSequenceOnTableau, FOUNDATION_SUIT_ORDER } from "./rules.js";
import type {
  Card,
  CardPile,
  DrawMode,
  FlipCard,
  GameState,
  Move,
  MoveToFoundation,
  MoveToTableau,
  Result,
} from "./types.js";
import { err, ok } from "./types.js";

// ============================================================
// Initiales Spielfeld aufbauen
// ============================================================

/**
 * Verteilt Karten nach Klondike-Regeln:
 *   Spalte 0: 1 Karte  (letzte aufgedeckt)
 *   Spalte 1: 2 Karten (letzte aufgedeckt)
 *   ...
 *   Spalte 6: 7 Karten (letzte aufgedeckt)
 * Verbleibende 24 Karten → Stock (alle verdeckt).
 */
export function createInitialGameState(
  gameId: string,
  seed: number,
  drawMode: DrawMode = 1
): GameState {
  const deck = createShuffledDeck(seed);

  const tableauColumns: Card[][] = [[], [], [], [], [], [], []];
  let deckIndex = 0;

  for (let col = 0; col < 7; col++) {
    for (let row = 0; row <= col; row++) {
      const card = deck[deckIndex++];
      if (card === undefined) throw new Error("Deck exhausted during deal");
      const isLastInColumn = row === col;
      tableauColumns[col]!.push({ ...card, faceUp: isLastInColumn });
    }
  }

  const stock: Card[] = deck.slice(deckIndex).map((c) => ({ ...c, faceUp: false }));

  return {
    gameId,
    seed,
    drawMode,
    stock,
    waste: [],
    foundation: [[], [], [], []],
    tableau: [
      tableauColumns[0]!,
      tableauColumns[1]!,
      tableauColumns[2]!,
      tableauColumns[3]!,
      tableauColumns[4]!,
      tableauColumns[5]!,
      tableauColumns[6]!,
    ],
    moves: 0,
    elapsedSeconds: 0,
    undoCount: 0,
    shakeCount: 0,
    flipCount: 0,
    stockRecycleCount: 0,
    status: "idle",
    history: [],
  };
}

// ============================================================
// Karte vom Stock ziehen
// ============================================================

/** Zieht 1 oder 3 Karten vom Stock auf den Waste-Stapel.
 *  Wenn Stock leer → Waste wird umgedreht und zum neuen Stock. */
export function drawFromStock(state: GameState): Result<GameState> {
  if (state.stock.length === 0) {
    if (state.waste.length === 0) {
      return err("Stock und Waste sind leer");
    }
    // Waste umdrehen → neuer Stock (gleiche Reihenfolge, verdeckt)
    const newStock = [...state.waste].reverse().map((c) => ({ ...c, faceUp: false }));
    const move: Move = { type: "RECYCLE_STOCK" };
    return ok({
      ...state,
      stock: newStock,
      waste: [],
      moves: state.moves + 1,
      stockRecycleCount: state.stockRecycleCount + 1,
      status: "playing",
      history: [...state.history, move],
    });
  }

  const count = Math.min(state.drawMode, state.stock.length);
  const drawn = state.stock.slice(-count).map((c) => ({ ...c, faceUp: true }));
  const move: Move = { type: "DRAW_FROM_STOCK" };

  return ok({
    ...state,
    stock: state.stock.slice(0, -count),
    waste: [...state.waste, ...drawn],
    moves: state.moves + 1,
    status: "playing",
    history: [...state.history, move],
  });
}

// ============================================================
// Karte auf Foundation legen
// ============================================================

export function applyMoveToFoundation(
  state: GameState,
  move: MoveToFoundation
): Result<GameState> {
  const { card, fromPile, toFoundationIndex } = move;
  const targetFoundation = state.foundation[toFoundationIndex];
  if (targetFoundation === undefined) return err("Ungültiger Foundation-Index");

  if (!canPlaceOnFoundation(card, targetFoundation, FOUNDATION_SUIT_ORDER[toFoundationIndex]!)) {
    return err(
      `Karte ${card.id} kann nicht auf Foundation ${toFoundationIndex} gelegt werden`
    );
  }

  const newFoundation = state.foundation.map((pile, i) =>
    i === toFoundationIndex ? [...pile, { ...card, faceUp: true }] : pile
  ) as [CardPile, CardPile, CardPile, CardPile];

  let newStock = state.stock;
  let newWaste = state.waste;
  let newTableau = state.tableau;

  if (fromPile.kind === "waste") {
    newWaste = state.waste.slice(0, -1);
  } else if (fromPile.kind === "tableau") {
    newTableau = removeTopCard(state.tableau, fromPile.columnIndex);
  }

  const newState: GameState = {
    ...state,
    stock: newStock,
    waste: newWaste,
    foundation: newFoundation,
    tableau: newTableau,
    moves: state.moves + 1,
    status: "playing",
    history: [...state.history, move],
  };

  return ok(checkWin(autoFlip(newState, fromPile)));
}

// ============================================================
// Karten im Tableau verschieben
// ============================================================

export function applyMoveToTableau(
  state: GameState,
  move: MoveToTableau
): Result<GameState> {
  const { cards, fromPile, toColumnIndex } = move;
  const targetColumn = state.tableau[toColumnIndex];
  if (targetColumn === undefined) return err("Ungültiger Tableau-Index");

  const valid =
    cards.length === 1
      ? canPlaceOnTableau(cards[0]!, targetColumn)
      : canPlaceSequenceOnTableau(cards, targetColumn);

  if (!valid) {
    return err(
      `Zug auf Tableau-Spalte ${toColumnIndex} ist ungültig`
    );
  }

  const newCards = cards.map((c) => ({ ...c, faceUp: true }));
  const newTableau = [...state.tableau] as unknown as [
    CardPile, CardPile, CardPile, CardPile, CardPile, CardPile, CardPile
  ];

  // Karten zum Ziel hinzufügen
  newTableau[toColumnIndex] = [...targetColumn, ...newCards];

  // Karten aus Quelle entfernen
  let newWaste = state.waste;
  if (fromPile.kind === "waste") {
    newWaste = state.waste.slice(0, -1);
  } else if (fromPile.kind === "tableau") {
    newTableau[fromPile.columnIndex] = state.tableau[fromPile.columnIndex]!.slice(
      0,
      state.tableau[fromPile.columnIndex]!.length - cards.length
    );
  }

  const newState: GameState = {
    ...state,
    waste: newWaste,
    tableau: newTableau,
    moves: state.moves + 1,
    status: "playing",
    history: [...state.history, move],
  };

  return ok(autoFlip(newState, fromPile));
}

// ============================================================
// Karte aufdecken
// ============================================================

export function applyFlipCard(state: GameState, move: FlipCard): Result<GameState> {
  const { tableauColumnIndex } = move;
  const column = state.tableau[tableauColumnIndex];
  if (column === undefined) return err("Ungültiger Tableau-Index");

  const topCard = column[column.length - 1];
  if (topCard === undefined) return err("Spalte ist leer");
  if (topCard.faceUp) return err("Karte ist bereits aufgedeckt");

  const newColumn = [...column.slice(0, -1), { ...topCard, faceUp: true }];
  const newTableau = state.tableau.map((col, i) =>
    i === tableauColumnIndex ? newColumn : col
  ) as unknown as GameState["tableau"];

  return ok({
    ...state,
    tableau: newTableau,
    moves: state.moves + 1,
    flipCount: state.flipCount + 1,
    history: [...state.history, move],
  });
}

// ============================================================
// Hilfsfunktionen
// ============================================================

type PileRefLike = { kind: string; columnIndex?: number };

/** Deckt automatisch die oberste verdeckte Karte einer Tableau-Spalte auf. */
function autoFlip(state: GameState, fromPile: PileRefLike): GameState {
  if (fromPile.kind !== "tableau" || fromPile.columnIndex === undefined) return state;
  const col = state.tableau[fromPile.columnIndex];
  if (col === undefined || col.length === 0) return state;
  const top = col[col.length - 1];
  if (top === undefined || top.faceUp) return state;

  const newColumn = [...col.slice(0, -1), { ...top, faceUp: true }];
  const newTableau = state.tableau.map((c, i) =>
    i === fromPile.columnIndex ? newColumn : c
  ) as unknown as GameState["tableau"];

  return { ...state, tableau: newTableau, flipCount: state.flipCount + 1 };
}

/** Entfernt die oberste Karte einer Tableau-Spalte. */
function removeTopCard(
  tableau: GameState["tableau"],
  columnIndex: number
): GameState["tableau"] {
  return tableau.map((col, i) =>
    i === columnIndex ? col.slice(0, -1) : col
  ) as unknown as GameState["tableau"];
}

/** Prüft ob das Spiel gewonnen ist (alle 52 Karten in den Foundations). */
function checkWin(state: GameState): GameState {
  const totalInFoundation = state.foundation.reduce((sum, pile) => sum + pile.length, 0);
  if (totalInFoundation === 52) {
    return { ...state, status: "won" };
  }
  return state;
}

/** Gibt true zurück wenn das Spiel gewonnen ist. */
export function isGameWon(state: GameState): boolean {
  return state.status === "won";
}

// ============================================================
// Auto-Complete: alle Karten automatisch auf Foundation legen
// ============================================================

/**
 * Gibt true zurück wenn die Auto-Complete-Bedingung erfüllt ist:
 * - Stock ist leer (keine verdeckten Karten mehr)
 * - Alle Tableau-Karten sind aufgedeckt
 */
export function canAutoComplete(state: GameState): boolean {
  if (state.status === "won") return false;
  if (state.stock.length > 0) return false;
  for (const col of state.tableau) {
    for (const card of col) {
      if (!card.faceUp) return false;
    }
  }
  return true;
}

/**
 * Findet einen Auto-Ass-Zug: Prüft ob ein sichtbares Ass (Rank 1) auf
 * eine leere Foundation gelegt werden kann. Waste zuerst, dann Tableau.
 * Gibt null zurück wenn kein Ass-Zug möglich ist.
 */
export function findAutoAceMove(state: GameState): Move | null {
  // Waste prüfen
  const topWaste = state.waste[state.waste.length - 1];
  if (topWaste?.rank === 1) {
    for (let i = 0; i < 4; i++) {
      if (canPlaceOnFoundation(topWaste, state.foundation[i]!, FOUNDATION_SUIT_ORDER[i]!)) {
        return {
          type: "MOVE_TO_FOUNDATION",
          card: topWaste,
          fromPile: { kind: "waste" },
          toFoundationIndex: i,
        };
      }
    }
  }

  // Tableau-Spalten prüfen (nur oberste sichtbare Karte jeder Spalte)
  for (let col = 0; col < 7; col++) {
    const column = state.tableau[col]!;
    const top = column[column.length - 1];
    if (top?.rank === 1) {
      for (let i = 0; i < 4; i++) {
        if (canPlaceOnFoundation(top, state.foundation[i]!, FOUNDATION_SUIT_ORDER[i]!)) {
          return {
            type: "MOVE_TO_FOUNDATION",
            card: top,
            fromPile: { kind: "tableau", columnIndex: col },
            toFoundationIndex: i,
          };
        }
      }
    }
  }

  return null;
}

/**
 * Findet den nächsten Auto-Complete-Zug (gieriger Greedy-Ansatz):
 * Prüft zuerst Waste, dann alle Tableau-Spalten auf Foundation-fähige Karten.
 * Gibt null zurück wenn kein Zug möglich ist.
 */
export function findAutoCompleteMove(state: GameState): Move | null {
  // Waste prüfen
  const topWaste = state.waste[state.waste.length - 1];
  if (topWaste) {
    for (let i = 0; i < 4; i++) {
      if (canPlaceOnFoundation(topWaste, state.foundation[i]!, FOUNDATION_SUIT_ORDER[i]!)) {
        return {
          type: "MOVE_TO_FOUNDATION",
          card: topWaste,
          fromPile: { kind: "waste" },
          toFoundationIndex: i,
        };
      }
    }
  }

  // Tableau-Spalten prüfen (oberste Karte jeder Spalte)
  for (let col = 0; col < 7; col++) {
    const column = state.tableau[col]!;
    const top = column[column.length - 1];
    if (top) {
      for (let i = 0; i < 4; i++) {
        if (canPlaceOnFoundation(top, state.foundation[i]!, FOUNDATION_SUIT_ORDER[i]!)) {
          return {
            type: "MOVE_TO_FOUNDATION",
            card: top,
            fromPile: { kind: "tableau", columnIndex: col },
            toFoundationIndex: i,
          };
        }
      }
    }
  }

  return null;
}

// ============================================================
// Punkte-System
// ============================================================

/**
 * Konfiguration für die Punkteberechnung.
 * Wird von außen (config.ts) übergeben — die Engine kennt keine App-Config.
 */
export interface ScoreConfig {
  /** Zeitlimit in Sekunden — wer darunter bleibt bekommt Zeitbonus. */
  readonly thresholdSeconds: number;
  /** Zeitbonus pro verbleibender Sekunde (bei Sieg). */
  readonly pointsPerSecond: number;
  /** Divisor für Zeitbonus wenn Set nicht abgeschlossen. */
  readonly notCompletedDivisor: number;
  /** Bonus pro Karte auf der Foundation. */
  readonly pointsPerFoundationCard: number;
  /** Einmaliger Bonus für alle 52 Karten abgelegt (Sieg). */
  readonly winBonus: number;
  /** Bonus pro aufgedeckter verdeckter Tableau-Karte. */
  readonly pointsPerFlip: number;
  /** Erste N Rotationen (Waste → Stock) ohne Abzug. */
  readonly rotationThreshold: number;
  /** Abzug pro Extra-Rotation ab rotationThreshold + 1. */
  readonly rotationPenalty: number;
  /** Abzug pro Shake (Doppelklick ohne gültiges Ziel). */
  readonly shakePenalty: number;
}

/** Aufschlüsselung aller Punktekomponenten. */
export interface ScoreBreakdown {
  /** Zeitbonus: verbleibende Sekunden × pointsPerSecond. */
  readonly timeBonus: number;
  /** Foundation-Bonus: Karten × pointsPerFoundationCard. */
  readonly foundationBonus: number;
  /** Einmaliger Gewinn-Bonus (nur bei Sieg). */
  readonly winBonus: number;
  /** Aufdeckungs-Bonus: flipCount × pointsPerFlip. */
  readonly flipBonus: number;
  /** Rotations-Abzug: Extra-Rotationen × rotationPenalty. */
  readonly rotationPenalty: number;
  /** Shake-Abzug: shakeCount × shakePenalty. */
  readonly shakePenalty: number;
  /** Gesamtpunktzahl (mindestens 0). */
  readonly total: number;
}

/** Registriert einen Shake (gescheiterter Doppelklick ohne Ziel). */
export function recordShake(state: GameState): GameState {
  return { ...state, shakeCount: state.shakeCount + 1 };
}

/**
 * Berechnet alle Punktekomponenten einzeln.
 * @param won  true = alle 52 Karten abgelegt, false = Set abgebrochen
 */
export function getScoreBreakdown(
  state: GameState,
  config: ScoreConfig,
  won = true
): ScoreBreakdown {
  const remaining = Math.max(0, config.thresholdSeconds - state.elapsedSeconds);
  const timeBonus = won
    ? remaining * config.pointsPerSecond
    : Math.floor((remaining * config.pointsPerSecond) / config.notCompletedDivisor);

  const cardsOnFoundation = state.foundation.reduce((sum, pile) => sum + pile.length, 0);
  const foundationBonus   = cardsOnFoundation * config.pointsPerFoundationCard;
  const winBonus          = won ? config.winBonus : 0;
  const flipBonus         = state.flipCount * config.pointsPerFlip;

  const extraRotations  = Math.max(0, state.stockRecycleCount - config.rotationThreshold);
  const rotationPenalty = extraRotations * config.rotationPenalty;
  const shakePenalty    = state.shakeCount * config.shakePenalty;

  const total = Math.max(
    0,
    timeBonus + foundationBonus + winBonus + flipBonus - rotationPenalty - shakePenalty
  );

  return { timeBonus, foundationBonus, winBonus, flipBonus, rotationPenalty, shakePenalty, total };
}

/**
 * Berechnet den Endpunktestand.
 * @param won  true = Sieg, false = Abbruch (Zeitbonus ÷ notCompletedDivisor)
 */
export function calculateScore(state: GameState, config: ScoreConfig, won = true): number {
  return getScoreBreakdown(state, config, won).total;
}

// ============================================================
// Zug-Dispatcher
// ============================================================

export function applyMove(state: GameState, move: Move): Result<GameState> {
  switch (move.type) {
    case "DRAW_FROM_STOCK":
    case "RECYCLE_STOCK":
      return drawFromStock(state);
    case "MOVE_TO_FOUNDATION":
      return applyMoveToFoundation(state, move);
    case "MOVE_TO_TABLEAU":
      return applyMoveToTableau(state, move);
    case "FLIP_CARD":
      return applyFlipCard(state, move);
  }
}

// ============================================================
// Undo: letzten Zug rückgängig machen
// ============================================================

/** Macht den letzten Zug rückgängig, indem das Spiel vom Anfang
 *  bis zum vorletzten Zug neu berechnet wird.
 *  Unterstützt sowohl seed-basierte als auch Custom-Layout-Spiele. */
export function undoLastMove(state: GameState): Result<GameState> {
  if (state.history.length === 0) return err("Keine Züge zum Rückgängigmachen");

  const previousMoves = state.history.slice(0, -1);

  // Startpunkt bestimmen: Custom-Layout oder Seed
  let currentState: GameState;
  if (state.layoutSource) {
    const result = createCustomGameState(state.gameId, state.layoutSource, state.drawMode);
    if (!result.ok) return err(`Fehler beim Undo-Replay: ${result.error}`);
    currentState = result.value;
  } else {
    currentState = createInitialGameState(state.gameId, state.seed, state.drawMode);
  }

  for (const move of previousMoves) {
    const result = applyMove(currentState, move);
    if (!result.ok) return err(`Fehler beim Replay: ${result.error}`);
    currentState = result.value;
  }

  return ok({
    ...currentState,
    elapsedSeconds: state.elapsedSeconds,
    undoCount: state.undoCount + 1,
  });
}
